import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, LogOut } from "lucide-react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { lookupCityInvite, acceptCityInvite } from "@/lib/city-teams.functions";

export const Route = createFileRoute("/city-invite/$token")({
  head: () => ({ meta: [
    { title: "Accept City Invitation — What Finland" },
    { name: "description", content: "Set up your city team account to manage a destination on What Finland." },
    { name: "robots", content: "noindex,nofollow" },
  ] }),
  component: ClaimPage,
});

type Lookup =
  | { ok: true; email: string; recipient_name: string | null; city_name: string; city_slug: string; expires_at: string }
  | { ok: false; error: string };

function ClaimPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<Lookup | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null;

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setSessionLoading(false);
    }).catch(() => setSessionLoading(false));

    const { data } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setSessionLoading(false);
    });
    subscription = data.subscription;

    lookupCityInvite({ data: { token } }).then((r) => setInvite(r as Lookup)).catch(() => setInvite({ ok: false, error: "invalid_token" }));

    return () => subscription?.unsubscribe();
  }, [token]);

  async function accept() {
    if (!invite || !invite.ok) return;
    setBusy(true);
    try {
      const res = (await acceptCityInvite({ data: { token } })) as { ok: boolean; error?: string };
      if (!res.ok) {
        if (res.error === "email_mismatch") {
          throw new Error("This invitation was sent to a different email address. Please sign out and use the invited account.");
        }
        throw new Error("Could not accept invite.");
      }
      toast.success(`Welcome — you now manage ${invite.city_name}.`);
      navigate({ to: "/city" as never, replace: true });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Something went wrong.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!invite || !invite.ok) return;
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      // Try sign up first; if the account exists, sign in with the entered password
      const su = await supabase.auth.signUp({
        email: invite.email,
        password,
        options: { data: { role: "city_editor", city_slug: invite.city_slug } },
      });
      if (su.error && !/registered|exists/i.test(su.error.message)) {
        throw su.error;
      }
      if (!su.data?.session) {
        const si = await supabase.auth.signInWithPassword({ email: invite.email, password });
        if (si.error) throw si.error;
      }
      await accept();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Something went wrong.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  async function signOutAndStartOver() {
    await supabase.auth.signOut();
    setSession(null);
    setPassword("");
    setConfirm("");
  }

  if (!invite || sessionLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (!invite.ok) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white shadow-lg rounded-2xl p-8 text-center">
          <h1 className="text-xl font-semibold text-slate-900">Invitation Unavailable</h1>
          <p className="mt-2 text-sm text-slate-600">
            {invite.error === "expired" ? "This invitation has expired. Ask the admin who invited you to send a new link." :
             invite.error === "already_accepted" ? "This invitation has already been accepted. Please sign in." :
             "We couldn't find this invitation."}
          </p>
        </div>
      </div>
    );
  }

  const sessionEmail = session?.user?.email ?? null;
  const emailMatches = sessionEmail && sessionEmail.toLowerCase() === invite.email.toLowerCase();

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white shadow-lg rounded-2xl p-8">
        <div className="mx-auto h-12 w-12 rounded-full bg-slate-900 text-white flex items-center justify-center">
          <MapPin className="h-5 w-5" />
        </div>
        <h1 className="mt-4 text-2xl font-semibold text-slate-900 text-center">Manage {invite.city_name}</h1>
        <p className="mt-2 text-sm text-slate-600 text-center">
          Set a password to activate your city team account. You'll be able to update the city profile, upload photos, and add points of interest.
        </p>

        {sessionEmail && (
          <div className="mt-4 rounded-xl bg-slate-100 px-3 py-2 text-xs text-slate-600 text-center">
            Signed in as <strong>{sessionEmail}</strong>
            {!emailMatches && (
              <span className="block mt-1 text-amber-600">
                This invite is for <strong>{invite.email}</strong>
              </span>
            )}
          </div>
        )}

        {emailMatches ? (
          <div className="mt-6 space-y-3">
            <button onClick={accept} disabled={busy}
              className="w-full rounded-2xl bg-slate-900 text-white font-medium px-5 py-3 hover:bg-slate-800 disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Accept & Continue
            </button>
            <button onClick={signOutAndStartOver} disabled={busy}
              className="w-full rounded-2xl border border-slate-200 text-slate-700 font-medium px-5 py-3 hover:bg-slate-50 inline-flex items-center justify-center gap-2">
              <LogOut className="h-4 w-4" /> Use a different account
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            {sessionEmail && !emailMatches && (
              <p className="text-xs text-amber-600 text-center">
                Please sign out first, or switch to the account this invitation was sent to.
              </p>
            )}
            <p className="text-xs text-slate-500 text-center">
              If you already have a What Finland account, enter your existing password.
            </p>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Password</label>
              <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Confirm Password</label>
              <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10" />
            </div>
            <button type="submit" disabled={busy}
              className="w-full rounded-2xl bg-slate-900 text-white font-medium px-5 py-3 hover:bg-slate-800 disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Accept & Continue
            </button>
            {sessionEmail && !emailMatches && (
              <button type="button" onClick={signOutAndStartOver} disabled={busy}
                className="w-full rounded-2xl border border-slate-200 text-slate-700 font-medium px-5 py-3 hover:bg-slate-50 inline-flex items-center justify-center gap-2">
                <LogOut className="h-4 w-4" /> Sign out and use {invite.email}
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
